// --
// -- How many rows a table shows, remembered per view.
// --
// -- Sits beside the column widths in localStorage and follows the same rules:
// -- one setting per view, per browser, and a stored value that makes no sense
// -- is ignored rather than allowed to break the table.
// --

export const PAGE_SIZE_OPTIONS = [10, 30, 50, 100, 200];

/** Past this a "page" is the whole result set; a hand-edited value is capped. */
const MAX_PAGE_SIZE = 500;

const storageKeyFor = (key?: string) => (key ? `table-page-size:${key}` : null);

/**
 * The size remembered for a view, or the fallback.
 *
 * Deliberately not checked against PAGE_SIZE_OPTIONS: that list can change, and
 * a size that is no longer offered is still a perfectly good size to be
 * showing - the selector adds it back rather than silently moving someone off
 * the setting they chose.
 */
export const readPageSize = (key: string | undefined, fallback: number): number => {
  const storageKey = storageKeyFor(key);
  if (!storageKey) return fallback;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return fallback;

    const size = Number(raw);
    return Number.isInteger(size) && size > 0 ? Math.min(size, MAX_PAGE_SIZE) : fallback;
  } catch (err) {
    // Private mode, cleared storage, a hand-edited value - fall back.
    return fallback;
  }
};

export const writePageSize = (key: string | undefined, size: number): void => {
  const storageKey = storageKeyFor(key);
  if (!storageKey) return;

  try {
    window.localStorage.setItem(storageKey, String(size));
  } catch (err) {
    // Not being able to remember the choice is not worth failing a render over.
  }
};

/** The offered sizes, including the current one when it is not among them. */
export const pageSizeOptions = (current: number): number[] =>
  PAGE_SIZE_OPTIONS.includes(current)
    ? PAGE_SIZE_OPTIONS
    : [...PAGE_SIZE_OPTIONS, current].sort((a, b) => a - b);
